import { Component, OnDestroy, OnInit } from '@angular/core';
import { catchError, finalize } from 'rxjs/operators';
import { firstValueFrom, Observable, of } from 'rxjs';

import { DemoService } from './../../../../demo.service';
import { LegalDateTimeService } from './../../../../services/legal-datetime.service';
import { HotToastService } from '@ngneat/hot-toast';
import {
  ActivityMetricsV2Breakdown,
  ActivityMetricsV2BackendDecision,
  ActivityMetricsV2DetailSection,
  ActivityMetricsV2ExportResponse,
  ActivityMetricsV2MetricResult,
  ActivityMetricsV2ResponseMode,
  ActivityMetricsV2Response,
  ActivityMetricsV2Search,
  ActivityMetricsV2StoreStatus,
  ActivityMetricsV2SubjectRollup,
} from './../../../../shared/models/activity-metrics-v2.model';
import {
  ACTIVITY_METRICS_V2_ATTEMPTS_INDEXED_GROUP_BY,
  ACTIVITY_METRICS_V2_DEFAULT_SOURCES,
  ACTIVITY_METRICS_V2_INDEXED_TRANSITION_TIMELINE_LIMIT,
  ActivityMetricsV2CoveragePresentation,
  applyActivityMetricsV2IndexedWindowToRequest,
  buildActivityMetricsV2CoveragePresentation,
  getActivityMetricsV2PreviousFullMonthWindow,
  resolveActivityMetricsV2IndexedThroughUtc,
} from './../activity-metrics-v2-indexed-reporting.util';

interface LegacyMetricCardConfig {
  card_key: string;
  card_label: string;
  card_title: string;
  metric_key: string;
  colour: string;
}

interface LegacyMetricChartRow {
  subject_number: string;
  metric_1: number | null;
  metric_2: number | null;
  metric_3: number | null;
  metric_4: number | null;
  metric_5: number | null;
  metric_6: number | null;
}

interface LegacyMetricSummaryChartRow {
  label: string;
  average_days: number | null;
  benchmark_days: number | null;
}

interface TimelineLimitOption {
  label: string;
  value: number;
}

interface LegacySubjectGridRow {
  job_id: number | null;
  subject_id: number | null;
  latest_subject_service_id: number | null | undefined;
  subject_number: string;
  subject_name: string;
  attempt_count: number;
  last_activity_at: string | null;
  latest_resolution_outcome_name: string | null | undefined;
  latest_service_type_name: string | null | undefined;
  region_source: string | null | undefined;
  job_status: string | null | undefined;
  has_stage_progression: boolean;
  was_spawned_from_subject: boolean;
  metric_1: string;
  metric_2: string;
  metric_3: string;
  metric_4: string;
  metric_5: string;
  metric_6: string;
}

interface LegacyBreakdownTable {
  key: string;
  label: string;
  rows: Array<{
    label: string;
    count: number;
    event_count: number | null | undefined;
    attempt_count: number | null | undefined;
    rate: number | null | undefined;
  }>;
}

interface LegacyTimelineRow {
  job_id: number | null | undefined;
  subject_id: number | null | undefined;
  subject_service_id: number | null | undefined;
  event_id: string;
  when: string | null;
  subject_number: string;
  subject_name: string;
  source: string;
  label: string;
  action: string | null | undefined;
  agent_name: string | null | undefined;
  service_type_name: string | null | undefined;
  resolution_outcome_name: string | null | undefined;
}

type ClassicIndexedLoadPhase = 'summary' | 'detail' | null;

@Component({
  selector: 'app-agent-performance-activity-metrics-attempts-indexed',
  templateUrl: './agent-performance-activity-metrics-attempts-indexed.component.html',
  styleUrls: ['./agent-performance-activity-metrics-attempts-indexed.component.scss']
})
export class AgentPerformanceActivityMetricsAttemptsIndexedComponent implements OnInit, OnDestroy {
  readonly backendTimelineRowCap = 5000;
  readonly subjectChartFocusLimit = 12;
  readonly splitResponseModeEnabled = true;
  readonly brand_fields: Object = { text: 'NameAndAddress', value: 'brand_id' };
  readonly dateFormat = 'dd/MM/yyyy hh:mm a';
  readonly defaultActivitySources = [...ACTIVITY_METRICS_V2_DEFAULT_SOURCES];
  readonly defaultGroupBy = [...ACTIVITY_METRICS_V2_ATTEMPTS_INDEXED_GROUP_BY];
  readonly timelineLimitOptions: TimelineLimitOption[] = [
    { label: '100', value: 100 },
    { label: '250', value: 250 },
    { label: '500', value: 500 },
    { label: 'All (max 5000)', value: 5000 },
  ];

  readonly metricCardConfig: LegacyMetricCardConfig[] = [
    {
      card_key: 'metric_1',
      card_label: 'Metric 1',
      card_title: 'Average No. Of Days From Instructions Sent To 1st Attempt',
      metric_key: 'attempt.time_instructions_to_first_actual',
      colour: '#457EDD',
    },
    {
      card_key: 'metric_2',
      card_label: 'Metric 2',
      card_title: 'Average No. Of Days From Instructions Sent To 2nd Attempt',
      metric_key: 'attempt.time_instructions_to_second_actual',
      colour: '#1F1F1F',
    },
    {
      card_key: 'metric_3',
      card_label: 'Metric 3',
      card_title: 'Average No. Of Days Between Agents 1st - 2nd Attempt',
      metric_key: 'attempt.time_first_to_second_actual',
      colour: '#A2D369',
    },
    {
      card_key: 'metric_4',
      card_label: 'Metric 4',
      card_title: 'Average No. Of Days From Instructions Sent To 3rd Attempt',
      metric_key: 'attempt.time_instructions_to_third_actual',
      colour: '#FA6F50',
    },
    {
      card_key: 'metric_5',
      card_label: 'Metric 5',
      card_title: 'Average No. Of Days Between Agents 2nd - 3rd Attempt',
      metric_key: 'attempt.time_second_to_third_actual',
      colour: '#FECF56',
    },
    {
      card_key: 'metric_6',
      card_label: 'Metric 6',
      card_title: 'Average No. Of Days Between Job Opened And Closed / Final Touch',
      metric_key: 'job.time_open_to_closed_or_last_touch',
      colour: '#62ADE5',
    },
  ];

  readonly supportMetricCardConfig: LegacyMetricCardConfig[] = [
    {
      card_key: 'support_loaded_to_open',
      card_label: 'Support 1',
      card_title: 'Job Loaded To Open',
      metric_key: 'job.time_loaded_to_open',
      colour: '#7F8C8D',
    },
    {
      card_key: 'support_open_to_first',
      card_label: 'Support 2',
      card_title: 'Job Opened To 1st Attempt',
      metric_key: 'attempt.time_open_to_first_actual',
      colour: '#457EDD',
    },
    {
      card_key: 'support_loaded_to_first',
      card_label: 'Support 3',
      card_title: 'Job Loaded To 1st Attempt',
      metric_key: 'attempt.time_loaded_to_first_actual',
      colour: '#5D6D7E',
    },
    {
      card_key: 'support_open_to_second',
      card_label: 'Support 4',
      card_title: 'Job Opened To 2nd Attempt',
      metric_key: 'attempt.time_open_to_second_actual',
      colour: '#1F1F1F',
    },
    {
      card_key: 'support_open_to_third',
      card_label: 'Support 5',
      card_title: 'Job Opened To 3rd Attempt',
      metric_key: 'attempt.time_open_to_third_actual',
      colour: '#FA6F50',
    },
    {
      card_key: 'support_loaded_to_closed',
      card_label: 'Support 6',
      card_title: 'Job Loaded To Closed / Last Touch',
      metric_key: 'job.time_loaded_to_closed_or_last_touch',
      colour: '#62ADE5',
    },
  ];

  readonly quickRangeDays = [1, 7, 14, 30, 90, 180, 365, 730];
  readonly monthOptions = [
    { index: 0, label: 'Jan' },
    { index: 1, label: 'Feb' },
    { index: 2, label: 'Mar' },
    { index: 3, label: 'Apr' },
    { index: 4, label: 'May' },
    { index: 5, label: 'Jun' },
    { index: 6, label: 'Jul' },
    { index: 7, label: 'Aug' },
    { index: 8, label: 'Sep' },
    { index: 9, label: 'Oct' },
    { index: 10, label: 'Nov' },
    { index: 11, label: 'Dec' },
  ];

  search_model: ActivityMetricsV2Search = this.createDefaultSearch();
  response: ActivityMetricsV2Response | null = null;
  store_status: ActivityMetricsV2StoreStatus | null = null;
  report_error_message: string | null = null;
  loading = false;
  loading_started_at_ms: number | null = null;
  loading_elapsed_seconds = 0;
  loading_timer: ReturnType<typeof setInterval> | null = null;
  loading_phase: ClassicIndexedLoadPhase = null;
  exporting = false;
  saving = false;
  store_status_loading = false;
  showAdvancedFilters = false;
  activeQuickRangeDays: number | null = null;
  selectedCalendarYear = new Date().getFullYear();
  activeMonthIndex: number | null = null;
  usingAnyIndexedDate = false;
  last_executed_request_model: ActivityMetricsV2Search | null = null;
  last_summary_request_model: ActivityMetricsV2Search | null = null;
  has_loaded_detail_payload = false;

  picker_when_from: Date | null = null;
  picker_when_to: Date | null = null;

  brands_agent: any[] = [];
  brands_client: any[] = [];
  lookup_warning_messages: string[] = [];

  constructor(
    private demoService: DemoService,
    public readonly legalDate: LegalDateTimeService,
    private readonly toastr: HotToastService,
  ) {}

  ngOnInit(): void {
    this.applyDefaultMonthWindow();
    this.loadLookups();
    this.loadStoreStatus();
  }

  ngOnDestroy(): void {
    this.stopLoadingTimer();
  }

  createDefaultSearch(): ActivityMetricsV2Search {
    return {
      when_from: null,
      when_to: null,
      limit: ACTIVITY_METRICS_V2_INDEXED_TRANSITION_TIMELINE_LIMIT,
      projection: 'ui',
      backend_mode: 'event_store',
      response_mode: this.splitResponseModeEnabled ? 'classic_summary' : 'full',
      detail_section: null,
      activity_sources: [...this.defaultActivitySources],
      brand_id_group: [],
      brand_report_id_group: [],
      subject_number_group: [],
      subject_number: null,
      subject_id: null,
      job_id: null,
      job_type_id_group: [],
      subject_service_type_id_group: [],
      subject_service_resolution_outcome_id_group: [],
      subject_service_outcome_id_group: [],
      state_group: [],
      postcode_group: [],
      suburb_group: [],
      group_by: [...this.defaultGroupBy],
      include_breakdowns: false,
      export_profile_key: 'timeline_audit',
      saved_file_name: 'agent-attempts-indexed-2-v2',
    };
  }

  loadLookups(): void {
    this.lookup_warning_messages = [];

    this.loadArrayLookup('Agent brands / groups', this.demoService.getBrandsComboBox({ brand_type_id: 3 }), (items) => {
      this.brands_agent = items;
    });
    this.loadArrayLookup('Reporting clients', this.demoService.getBrandsComboBox({ brand_type_id: 2 }), (items) => {
      this.brands_client = items;
    });
  }

  async loadMetrics(): Promise<void> {
    const requestModel = this.buildRequestModel({
      responseMode: this.splitResponseModeEnabled ? 'classic_summary' : 'full',
      detailSection: null,
    });
    this.loading = true;
    this.loading_phase = 'summary';
    this.loading_started_at_ms = Date.now();
    this.loading_elapsed_seconds = 0;
    this.startLoadingTimer();
    this.report_error_message = null;
    this.response = null;
    this.has_loaded_detail_payload = false;
    this.last_executed_request_model = null;
    this.last_summary_request_model = null;

    try {
      const status = await this.fetchStoreStatus(requestModel);
      const coveragePresentation = this.buildCoveragePresentation(requestModel, status);
      if (
        !coveragePresentation.hasIndexedCoverage
        || !coveragePresentation.effectiveWindowFromUtc
        || !coveragePresentation.effectiveWindowToUtc
      ) {
        this.response = null;
        this.report_error_message = coveragePresentation.detail;
        return;
      }

      const executionRequestModel = applyActivityMetricsV2IndexedWindowToRequest(requestModel, coveragePresentation);
      const response = await firstValueFrom(this.demoService.getAgentActivityMetricsV2(executionRequestModel));
      this.response = response;
      this.report_error_message = null;
      this.last_executed_request_model = executionRequestModel;
      this.last_summary_request_model = executionRequestModel;
      this.has_loaded_detail_payload = !this.splitResponseModeEnabled || (executionRequestModel.response_mode ?? 'full') === 'full';
    } catch (error: any) {
      this.report_error_message = error?.error?.detail || error?.message || 'Failed to load indexed Attempts Dashboard 2.0.';
    } finally {
      this.loading = false;
      this.loading_phase = null;
      this.loading_started_at_ms = null;
      this.stopLoadingTimer();
    }
  }

  async loadDetailMetrics(): Promise<void> {
    if (!this.response || !this.last_summary_request_model) {
      this.report_error_message = 'Load summary results before requesting classic detail.';
      return;
    }

    const requestModel = this.buildRequestModel({
      base: this.last_summary_request_model,
      responseMode: 'classic_detail',
      detailSection: null,
    });

    this.loading = true;
    this.loading_phase = 'detail';
    this.loading_started_at_ms = Date.now();
    this.loading_elapsed_seconds = 0;
    this.startLoadingTimer();
    this.report_error_message = null;

    try {
      const response = await firstValueFrom(this.demoService.getAgentActivityMetricsV2(requestModel));
      this.response = response;
      this.last_executed_request_model = requestModel;
      this.has_loaded_detail_payload = true;
    } catch (error: any) {
      this.report_error_message = error?.error?.detail || error?.message || 'Failed to load classic indexed detail payload.';
    } finally {
      this.loading = false;
      this.loading_phase = null;
      this.loading_started_at_ms = null;
      this.stopLoadingTimer();
    }
  }

  resetSearch(): void {
    this.search_model = this.createDefaultSearch();
    this.response = null;
    this.report_error_message = null;
    this.last_executed_request_model = null;
    this.last_summary_request_model = null;
    this.has_loaded_detail_payload = false;
    this.applyDefaultMonthWindow();
    this.loadStoreStatus();
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters = !this.showAdvancedFilters;
  }

  applyQuickRange(days: number, reload = true): void {
    const to = this.legalDate.toPickerDate(new Date()) ?? new Date();
    const from = new Date(to.getTime());
    from.setDate(from.getDate() - days);

    this.activeQuickRangeDays = days;
    this.usingAnyIndexedDate = false;
    this.picker_when_from = from;
    this.picker_when_to = to;
    this.syncCalendarSelection();

    if (reload) {
      this.loadMetrics();
    }
  }

  applyDefaultMonthWindow(): void {
    const defaultWindow = getActivityMetricsV2PreviousFullMonthWindow(this.legalDate.toPickerDate(new Date()) ?? new Date());
    this.selectedCalendarYear = defaultWindow.year;
    this.activeQuickRangeDays = null;
    this.activeMonthIndex = defaultWindow.monthIndex;
    this.usingAnyIndexedDate = false;
    this.picker_when_from = defaultWindow.from;
    this.picker_when_to = defaultWindow.to;
    this.syncCalendarSelection();
  }

  async applyAnyIndexedDate(reload = true): Promise<void> {
    this.report_error_message = null;

    try {
      const status = await this.fetchStoreStatus(this.buildRequestModel());
      const earliestIndexedUtc = status?.earliest_indexed_utc ?? null;
      const indexedThroughUtc = resolveActivityMetricsV2IndexedThroughUtc(status, this.defaultActivitySources);

      if (!earliestIndexedUtc || !indexedThroughUtc) {
        this.report_error_message = 'Any Indexed Date is not available yet because the indexed date bounds could not be resolved for the selected source set.';
        return;
      }

      const whenFrom = this.legalDate.toPickerDate(earliestIndexedUtc);
      const whenTo = this.legalDate.toPickerDate(indexedThroughUtc);

      if (!whenFrom || !whenTo) {
        this.report_error_message = 'Any Indexed Date could not convert the indexed date bounds into a usable reporting window.';
        return;
      }

      this.activeQuickRangeDays = null;
      this.activeMonthIndex = null;
      this.usingAnyIndexedDate = true;
      this.search_model.limit = Math.min(this.search_model.limit || ACTIVITY_METRICS_V2_INDEXED_TRANSITION_TIMELINE_LIMIT, ACTIVITY_METRICS_V2_INDEXED_TRANSITION_TIMELINE_LIMIT);
      this.picker_when_from = whenFrom;
      this.picker_when_to = whenTo;
      this.syncCalendarSelection();
      this.activeMonthIndex = null;

      if (reload) {
        await this.loadMetrics();
      } else {
        this.loadStoreStatus(this.buildRequestModel());
      }
    } catch (error: any) {
      this.report_error_message = error?.error?.detail || error?.message || 'Failed to resolve Any Indexed Date for indexed attempts reporting.';
    }
  }

  setMonthWindow(monthIndex: number): void {
    const whenFrom = new Date(this.selectedCalendarYear, monthIndex, 1, 0, 0, 0, 0);
    const whenTo = new Date(this.selectedCalendarYear, monthIndex + 1, 0, 23, 59, 0, 0);

    this.activeQuickRangeDays = null;
    this.activeMonthIndex = monthIndex;
    this.usingAnyIndexedDate = false;
    this.picker_when_from = whenFrom;
    this.picker_when_to = whenTo;
  }

  onCalendarYearChange(year: number | string): void {
    const numericYear = Number(year);
    if (!Number.isFinite(numericYear)) {
      return;
    }

    this.selectedCalendarYear = numericYear;

    if (this.activeMonthIndex !== null) {
      this.setMonthWindow(this.activeMonthIndex);
    }
  }

  onPickerChange(): void {
    this.activeQuickRangeDays = null;
    this.usingAnyIndexedDate = false;
    this.syncCalendarSelection();
  }

  async exportMetrics(): Promise<void> {
    const requestModel: ActivityMetricsV2Search = {
      ...this.buildRequestModel({
        responseMode: 'full',
        detailSection: null,
      }),
      projection: 'audit',
    };

    this.exporting = true;
    try {
      const status = await this.fetchStoreStatus(requestModel);
      const coveragePresentation = this.buildCoveragePresentation(requestModel, status);
      if (
        !coveragePresentation.hasIndexedCoverage
        || !coveragePresentation.effectiveWindowFromUtc
        || !coveragePresentation.effectiveWindowToUtc
      ) {
        this.report_error_message = coveragePresentation.detail;
        return;
      }

      const executionRequestModel = applyActivityMetricsV2IndexedWindowToRequest(requestModel, coveragePresentation);
      const response = await firstValueFrom(this.demoService.exportAgentActivityMetricsV2(executionRequestModel));
      this.downloadExportFile(response);
    } catch (error: any) {
      this.report_error_message = error?.error?.detail || error?.message || 'Failed to export Metrics 2.0 classic view.';
    } finally {
      this.exporting = false;
    }
  }

  saveFootprint(): void {
    if (!this.response) {
      this.report_error_message = 'Load indexed data before saving a footprint.';
      return;
    }

    if (!this.hasLoadedDetailPayload) {
      this.report_error_message = 'Load classic detail before saving the indexed footprint so the saved payload remains complete.';
      return;
    }

    if (!window.confirm('Save indexed attempts metrics as a footprint?')) {
      return;
    }

    const requestModel = this.last_executed_request_model ?? this.buildRequestModel();
    this.saving = true;
    this.demoService.saveAgentAttemptsMetricsData({
      data: this.response,
      search_model: requestModel,
      saved_file_name: this.search_model.saved_file_name || 'agent-attempts-indexed-2-v2',
    })
      .pipe(finalize(() => {
        this.saving = false;
      }))
      .subscribe({
        next: () => {
          this.toastr.success('Indexed attempts footprint saved.');
        },
        error: (error) => {
          this.report_error_message = error?.error?.detail || error?.message || 'Failed to save indexed attempts footprint.';
        },
      });
  }

  loadStoreStatus(requestModel?: ActivityMetricsV2Search | null): void {
    this.store_status_loading = true;

    const statusRequest = requestModel === null
      ? this.demoService.getAgentActivityMetricsV2StoreStatus()
      : this.demoService.getAgentActivityMetricsV2StoreStatus(requestModel ?? this.buildRequestModel());

    statusRequest
      .pipe(finalize(() => {
        this.store_status_loading = false;
      }))
      .subscribe({
        next: (status) => {
          this.store_status = status;
        },
        error: () => {
          this.store_status = null;
        },
      });
  }

  buildRequestModel(options: {
    base?: ActivityMetricsV2Search | null;
    responseMode?: ActivityMetricsV2ResponseMode;
    detailSection?: ActivityMetricsV2DetailSection | null;
  } = {}): ActivityMetricsV2Search {
    const requestModel: ActivityMetricsV2Search = {
      ...(options.base ?? this.search_model),
      backend_mode: 'event_store',
      response_mode: options.responseMode ?? this.search_model.response_mode ?? (this.splitResponseModeEnabled ? 'classic_summary' : 'full'),
      detail_section: options.detailSection ?? null,
      when_from: options.base
        ? (options.base.when_from ?? null)
        : (this.picker_when_from ? this.legalDate.fromPickerDate(this.picker_when_from) : null),
      when_to: options.base
        ? (options.base.when_to ?? null)
        : (this.picker_when_to ? this.legalDate.fromPickerDate(this.picker_when_to) : null),
      activity_sources: [...this.defaultActivitySources],
      group_by: [...this.defaultGroupBy],
    };

    requestModel.classic_lightweight = requestModel.response_mode === 'classic_summary'
      ? false
      : this.isBroadUiLightweightRequest(requestModel);

    return requestModel;
  }

  get calendarYearOptions(): number[] {
    const currentYear = this.legalDate.toPickerDate(new Date())?.getFullYear() ?? new Date().getFullYear();
    const earliestYear = Math.max(2020, currentYear - 8);
    const years: number[] = [];

    for (let year = currentYear; year >= earliestYear; year -= 1) {
      years.push(year);
    }

    return years;
  }

  get metricCards(): Array<LegacyMetricCardConfig & { result: ActivityMetricsV2MetricResult | undefined }> {
    return this.metricCardConfig.map((config) => ({
      ...config,
      result: this.getMetricResult(config.metric_key),
    }));
  }

  get supportMetricCards(): Array<LegacyMetricCardConfig & { result: ActivityMetricsV2MetricResult | undefined }> {
    return this.supportMetricCardConfig.map((config) => ({
      ...config,
      result: this.getMetricResult(config.metric_key),
    }));
  }

  get totalAttempts(): number {
    return this.response?.summary?.total_attempts ?? 0;
  }

  get totalSubjects(): number {
    return this.response?.summary?.total_subjects ?? 0;
  }

  get totalAgents(): number {
    return this.response?.summary?.total_agents ?? 0;
  }

  get hasLoadedReport(): boolean {
    return !!this.response;
  }

  get hasLoadedDetailPayload(): boolean {
    return this.hasLoadedReport && this.has_loaded_detail_payload;
  }

  get canLoadDetails(): boolean {
    return this.splitResponseModeEnabled && this.hasLoadedReport && !this.hasLoadedDetailPayload && !this.loading;
  }

  get showDetailPrompt(): boolean {
    return this.splitResponseModeEnabled && this.hasLoadedReport && !this.hasLoadedDetailPayload;
  }

  get resolvedBackendLabel(): string {
    return this.formatBackendMode(this.backendDecision?.resolved_backend_mode ?? this.search_model.backend_mode ?? 'auto');
  }

  get requestedBackendLabel(): string {
    return this.formatBackendMode(this.search_model.backend_mode ?? 'auto');
  }

  get backendDecision(): ActivityMetricsV2BackendDecision | null {
    return this.response?.diagnostics?.backend_decision ?? null;
  }

  get storeCoverage() {
    return this.store_status?.coverage ?? null;
  }

  get coveragePresentation() {
    return this.buildCoveragePresentation(this.buildRequestModel(), this.store_status);
  }

  get indexedThroughUtc(): string | null {
    return resolveActivityMetricsV2IndexedThroughUtc(this.store_status, this.defaultActivitySources);
  }

  get earliestIndexedUtc(): string | null {
    return this.store_status?.earliest_indexed_utc ?? null;
  }

  get indexedStatusSummary(): string {
    if (this.store_status_loading) {
      return 'Checking indexed coverage for the selected window...';
    }

    if (!this.store_status) {
      return 'Indexed coverage has not been checked yet for the selected window.';
    }

    return this.coveragePresentation.detail;
  }

  get backendReasonText(): string | null {
    if (!this.backendDecision?.reason) {
      return null;
    }

    if (this.coveragePresentation.isClipped) {
      return 'Reporting Tables resolved the indexed execution window for the overlapping indexed slice. The original requested window remains partially indexed; see the indexed boundary message below.';
    }

    return this.backendDecision.reason;
  }

  get backendStatusAlertClass(): string {
    if (this.loading) {
      return 'alert alert-warning classic-runtime-alert';
    }

    if (this.report_error_message && !this.response) {
      return 'alert alert-danger classic-runtime-alert';
    }

    return this.coveragePresentation.alertClass;
  }

  private syncCalendarSelection(): void {
    const whenFrom = this.picker_when_from;
    const whenTo = this.picker_when_to;

    if (!whenFrom || !whenTo) {
      this.activeMonthIndex = null;
      return;
    }

    this.selectedCalendarYear = whenFrom.getFullYear();

    const isSameMonth = whenFrom.getFullYear() === whenTo.getFullYear()
      && whenFrom.getMonth() === whenTo.getMonth();
    const isMonthStart = whenFrom.getDate() === 1
      && whenFrom.getHours() === 0
      && whenFrom.getMinutes() === 0;
    const expectedMonthEnd = new Date(whenFrom.getFullYear(), whenFrom.getMonth() + 1, 0, 23, 59, 0, 0);
    const isMonthEnd = whenTo.getFullYear() === expectedMonthEnd.getFullYear()
      && whenTo.getMonth() === expectedMonthEnd.getMonth()
      && whenTo.getDate() === expectedMonthEnd.getDate()
      && whenTo.getHours() === expectedMonthEnd.getHours()
      && whenTo.getMinutes() === expectedMonthEnd.getMinutes();

    this.activeMonthIndex = isSameMonth && isMonthStart && isMonthEnd
      ? whenFrom.getMonth()
      : null;
  }

  private buildCoveragePresentation(
    requestModel: ActivityMetricsV2Search,
    status: ActivityMetricsV2StoreStatus | null,
  ): ActivityMetricsV2CoveragePresentation {
    return buildActivityMetricsV2CoveragePresentation({
      status,
      requestedSources: requestModel.activity_sources,
      requestedWhenFrom: requestModel.when_from,
      requestedWhenTo: requestModel.when_to,
      formatDate: (value: string) => this.legalDate.formatAudit(value, 'DD/MM/YYYY h:mm a'),
      formatDuration: (value: number) => this.formatDuration(value),
    });
  }

  get backendRuntimeSummary(): string {
    const responseMode = this.response?.diagnostics?.response_mode ?? (this.last_executed_request_model?.response_mode ?? this.search_model.response_mode ?? 'full');
    const summaryParts = [
      this.usingAnyIndexedDate
        ? 'Any Indexed Date'
        : `Default window ${this.estimateWindowDays()} day${this.estimateWindowDays() === 1 ? '' : 's'}`,
      'Requested Reporting Tables',
      `Resolved ${this.resolvedBackendLabel}`,
      `Mode ${responseMode}`,
      this.search_model.limit >= this.backendTimelineRowCap
        ? `Timeline rows all (cap ${this.backendTimelineRowCap})`
        : `Timeline rows ${this.search_model.limit}`,
    ];

    if (this.storeCoverage?.coverage_status) {
      summaryParts.splice(3, 0, `Coverage ${this.storeCoverage.coverage_status}`);
    }

    return summaryParts.join(' | ');
  }

  get benchmarkSummaryCards(): Array<LegacyMetricCardConfig & {
    result: ActivityMetricsV2MetricResult | undefined;
    difference_days: number | null;
  }> {
    return this.metricCardConfig.map((config) => {
      const result = this.getMetricResult(config.metric_key);
      return {
        ...config,
        result,
        difference_days: this.msToDays(result?.difference_ms ?? null),
      };
    });
  }

  get loadingStatusMessage(): string {
    const sourceCount = this.search_model.activity_sources?.length ?? 0;
    const daySpan = this.estimateWindowDays();
    if (this.loading_phase === 'detail') {
      return `Fetching classic detail payload across ${sourceCount} source${sourceCount === 1 ? '' : 's'} for roughly ${daySpan} day${daySpan === 1 ? '' : 's'}.`;
    }

    return `Fetching summary metric results across ${sourceCount} source${sourceCount === 1 ? '' : 's'} for roughly ${daySpan} day${daySpan === 1 ? '' : 's'}.`;
  }

  get loadingStatusDetail(): string {
    const responseMode = this.loading_phase === 'detail'
      ? 'classic_detail'
      : (this.splitResponseModeEnabled ? 'classic_summary' : 'full');
    return `${this.backendRuntimeSummary} | Projection ${this.search_model.projection?.toUpperCase?.() || 'UI'} | Response ${responseMode}`;
  }

  get breakdownsDisabledMessage(): string {
    return 'Breakdown payloads are disabled for speed on the default classic indexed run. Turn them back on only when you need those grouped tables.';
  }

  get durationLabel(): string {
    if (!this.picker_when_from || !this.picker_when_to) {
      return '-';
    }

    const totalMs = this.picker_when_to.getTime() - this.picker_when_from.getTime();
    if (totalMs <= 0) {
      return '0m';
    }

    const totalMinutes = Math.floor(totalMs / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    const parts: string[] = [];

    if (days > 0) {
      parts.push(`${days} D`);
    }

    if (hours > 0) {
      parts.push(`${hours} H`);
    }

    if (minutes > 0 || !parts.length) {
      parts.push(`${minutes} M`);
    }

    return parts.join(' ');
  }

  get subjectMetricChartRows(): LegacyMetricChartRow[] {
    if (!this.hasLoadedDetailPayload) {
      return [];
    }

    return (this.response?.subjects ?? [])
      .slice()
      .sort((left, right) => (right.attempt_count ?? 0) - (left.attempt_count ?? 0))
      .slice(0, this.subjectChartFocusLimit)
      .map((subject) => ({
        subject_number: subject.subject_number || `Subject ${subject.subject_id ?? '-'}`,
        metric_1: this.metricValueInDays(subject, 'attempt.time_instructions_to_first_actual'),
        metric_2: this.metricValueInDays(subject, 'attempt.time_instructions_to_second_actual'),
        metric_3: this.metricValueInDays(subject, 'attempt.time_first_to_second_actual'),
        metric_4: this.metricValueInDays(subject, 'attempt.time_instructions_to_third_actual'),
        metric_5: this.metricValueInDays(subject, 'attempt.time_second_to_third_actual'),
        metric_6: this.metricValueInDays(subject, 'job.time_open_to_closed_or_last_touch'),
      }));
  }

  get showSubjectChartDataLabels(): boolean {
    return this.subjectMetricChartRows.length > 0 && this.subjectMetricChartRows.length <= this.subjectChartFocusLimit;
  }

  get subjectChartMarkerSettings(): { visible: boolean; dataLabel?: { visible: boolean; position: string } } {
    if (this.showSubjectChartDataLabels) {
      return {
        visible: true,
        dataLabel: {
          visible: true,
          position: 'Top',
        },
      };
    }

    return {
      visible: true,
    };
  }

  get isSubjectChartFocused(): boolean {
    return (this.response?.subjects?.length ?? 0) > this.subjectChartFocusLimit;
  }

  get subjectChartModeMessage(): string | null {
    if (!this.response?.subjects?.length) {
      return null;
    }

    if (this.isSubjectChartFocused) {
      return `Showing the top ${this.subjectChartFocusLimit} subjects by attempt volume in the chart. Use the Subject Metrics Grid below for the full numeric set.`;
    }

    return 'Chart labels are shown because the current subject set is small enough to remain readable.';
  }

  get metricSummaryChartRows(): LegacyMetricSummaryChartRow[] {
    return this.metricCardConfig.map((config) => {
      const result = this.getMetricResult(config.metric_key);
      return {
        label: config.card_label,
        average_days: this.msToDays(result?.average_ms ?? null),
        benchmark_days: this.msToDays(result?.benchmark_ms ?? null),
      };
    });
  }

  get lifecycleBreakdownTables(): LegacyBreakdownTable[] {
    if (!this.hasLoadedDetailPayload) {
      return [];
    }

    if (!this.search_model.include_breakdowns) {
      return [];
    }

    return [
      this.mapBreakdownTable('attempt_lifecycle', 'Attempt Lifecycle'),
      this.mapBreakdownTable('subject_lifecycle', 'Subject Lifecycle'),
      this.mapBreakdownTable('service_type', 'Service Type'),
      this.mapBreakdownTable('resolution_outcome', 'Resolution Outcome'),
      this.mapBreakdownTable('resolution_result', 'Resolution Result'),
      this.mapBreakdownTable('state', 'State'),
    ].filter((table): table is LegacyBreakdownTable => !!table);
  }

  get subjectGridRows(): LegacySubjectGridRow[] {
    if (!this.hasLoadedDetailPayload) {
      return [];
    }

    return (this.response?.subjects ?? [])
      .slice()
      .sort((left, right) => (right.attempt_count ?? 0) - (left.attempt_count ?? 0))
      .slice(0, 25)
      .map((subject) => ({
        job_id: subject.job_id ?? null,
        subject_id: subject.subject_id ?? null,
        latest_subject_service_id: subject.latest_subject_service_id,
        subject_number: subject.subject_number || '-',
        subject_name: subject.subject_name || '-',
        attempt_count: subject.attempt_count ?? 0,
        last_activity_at: subject.last_activity_at ?? null,
        latest_resolution_outcome_name: subject.latest_resolution_outcome_name,
        latest_service_type_name: subject.latest_service_type_name,
        region_source: subject.region_source,
        job_status: subject.job_status,
        has_stage_progression: !!subject.has_stage_progression,
        was_spawned_from_subject: !!subject.was_spawned_from_subject,
        metric_1: this.formatMetricValue(subject, 'attempt.time_instructions_to_first_actual'),
        metric_2: this.formatMetricValue(subject, 'attempt.time_instructions_to_second_actual'),
        metric_3: this.formatMetricValue(subject, 'attempt.time_first_to_second_actual'),
        metric_4: this.formatMetricValue(subject, 'attempt.time_instructions_to_third_actual'),
        metric_5: this.formatMetricValue(subject, 'attempt.time_second_to_third_actual'),
        metric_6: this.formatMetricValue(subject, 'job.time_open_to_closed_or_last_touch'),
      }));
  }

  get timelineRows(): LegacyTimelineRow[] {
    if (!this.hasLoadedDetailPayload) {
      return [];
    }

    return (this.response?.timeline ?? [])
      .slice(0, this.search_model.limit)
      .map((event) => ({
        job_id: event.job_id,
        subject_id: event.subject_id,
        subject_service_id: event.subject_service_id,
        event_id: event.event_id,
        when: event.canonical_timestamp ?? null,
        subject_number: event.subject_number || '-',
        subject_name: event.subject_name || '-',
        source: event.source || '-',
        label: event.label || '-',
        action: event.action,
        agent_name: event.agent_name,
        service_type_name: event.service_type_name,
        resolution_outcome_name: event.resolution_outcome_name,
      }));
  }

  get sourceCountLabels(): string[] {
    return (this.response?.summary?.source_counts ?? []).map((sourceCount) => {
      return `${sourceCount.label} ${sourceCount.count}`;
    });
  }

  trackByMetricCard(index: number, item: LegacyMetricCardConfig): string {
    return item.card_key;
  }

  trackBySubjectNumber(index: number, item: LegacyMetricChartRow | LegacySubjectGridRow): string {
    return item.subject_number;
  }

  quickRangeLabel(days: number): string {
    return days === 1 ? 'Past 24 hrs' : days === 730 ? 'Past 2 YRS' : `Past ${days} Days`;
  }

  private loadArrayLookup<T>(label: string, request$: Observable<T>, assign: (items: any[]) => void): void {
    request$
      .pipe(
        catchError((error) => {
          const detail = error?.error?.detail || error?.message || 'Lookup request failed.';
          this.lookup_warning_messages = [...this.lookup_warning_messages, `${label}: ${detail}`];
          assign([]);
          return of([]);
        })
      )
      .subscribe((result) => {
        assign(Array.isArray(result) ? result : []);
      });
  }

  private async fetchStoreStatus(requestModel: ActivityMetricsV2Search): Promise<ActivityMetricsV2StoreStatus | null> {
    this.store_status_loading = true;

    try {
      const status = await firstValueFrom(this.demoService.getAgentActivityMetricsV2StoreStatus(requestModel));
      this.store_status = status;
      return status;
    } finally {
      this.store_status_loading = false;
    }
  }

  private isBroadUiLightweightRequest(requestModel: ActivityMetricsV2Search): boolean {
    if ((requestModel.projection ?? 'ui') !== 'ui') {
      return false;
    }

    if (requestModel.include_breakdowns) {
      return false;
    }

    if (requestModel.hosted_context_panel) {
      return false;
    }

    const hasExactDrilldown = !!requestModel.subject_id
      || !!requestModel.job_id
      || !!(requestModel.subject_number && String(requestModel.subject_number).trim().length)
      || !!(requestModel.subject_number_group?.length);

    return !hasExactDrilldown;
  }

  private startLoadingTimer(): void {
    this.stopLoadingTimer();
    this.loading_timer = setInterval(() => {
      if (!this.loading_started_at_ms) {
        this.loading_elapsed_seconds = 0;
        return;
      }

      this.loading_elapsed_seconds = Math.max(0, Math.floor((Date.now() - this.loading_started_at_ms) / 1000));
    }, 1000);
  }

  private stopLoadingTimer(): void {
    if (this.loading_timer) {
      clearInterval(this.loading_timer);
      this.loading_timer = null;
    }
  }

  private estimateWindowDays(): number {
    if (!this.picker_when_from || !this.picker_when_to) {
      return 0;
    }

    const fromTime = this.picker_when_from.getTime();
    const toTime = this.picker_when_to.getTime();
    if (!Number.isFinite(fromTime) || !Number.isFinite(toTime) || toTime < fromTime) {
      return 0;
    }

    return Math.max(1, Math.ceil((toTime - fromTime) / 86400000));
  }

  private mapBreakdownTable(key: string, label: string): LegacyBreakdownTable | null {
    const breakdown = this.getBreakdown(key);
    if (!breakdown?.rows?.length) {
      return null;
    }

    return {
      key,
      label,
      rows: breakdown.rows.slice(0, 20).map((row) => ({
        label: row.label,
        count: row.count,
        event_count: row.event_count,
        attempt_count: row.attempt_count,
        rate: row.rate,
      })),
    };
  }

  private formatBackendMode(mode: ActivityMetricsV2Search['backend_mode'] | null | undefined): string {
    if (mode === 'event_store') {
      return 'Reporting Tables';
    }

    if (mode === 'live') {
      return 'Live Tables';
    }

    return 'Auto';
  }

  private getMetricResult(metricKey: string): ActivityMetricsV2MetricResult | undefined {
    return this.response?.metric_results?.find((metric) => metric.metric_key === metricKey);
  }

  private getBreakdown(breakdownKey: string): ActivityMetricsV2Breakdown | undefined {
    return this.response?.grouped_breakdowns?.find((breakdown) => breakdown.breakdown_key === breakdownKey);
  }

  private metricValueInDays(subject: ActivityMetricsV2SubjectRollup, metricKey: string): number | null {
    const rawValue = subject.metric_values?.[metricKey];
    return this.msToDays(rawValue);
  }

  private formatMetricValue(subject: ActivityMetricsV2SubjectRollup, metricKey: string): string {
    const rawValue = subject.metric_values?.[metricKey];

    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return '-';
    }

    if (typeof rawValue === 'number') {
      return this.formatDuration(rawValue);
    }

    return `${rawValue}`;
  }

  private msToDays(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numericValue)) {
      return null;
    }

    return Number((numericValue / 86400000).toFixed(2));
  }

  private formatDuration(ms: number): string {
    if (!Number.isFinite(ms) || ms <= 0) {
      return '0m';
    }

    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const parts: string[] = [];

    if (days > 0) {
      parts.push(`${days}d`);
    }

    if (hours > 0) {
      parts.push(`${hours}h`);
    }

    if (minutes > 0) {
      parts.push(`${minutes}m`);
    }

    if (!parts.length) {
      parts.push(`${totalSeconds}s`);
    }

    return parts.join(' ');
  }

  private downloadExportFile(response: ActivityMetricsV2ExportResponse): void {
    const rows = Array.isArray(response.rows) ? response.rows : [];
    const columns = Array.isArray(response.columns) ? response.columns : [];

    if (!rows.length || !columns.length) {
      return;
    }

    const csvLines = [
      columns.join(','),
      ...rows.map((row) => columns.map((column) => this.escapeCsvValue(row[column])).join(',')),
    ];

    const blob = new Blob([csvLines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${response.profile?.file_name || this.search_model.saved_file_name || 'activity-metrics-v2-classic'}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  private escapeCsvValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    const stringValue = `${value}`.replace(/"/g, '""');
    return `"${stringValue}"`;
  }
}
